import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'
import { InlineEditPayload, InlineEditResponse, InlineEditResponseSchema } from '../store/schemas'

const kInlineEditPath = '/api/v1/artifact/versions/{artifactVersionId}/inline-edit'

export async function postInlineEdit(conversationId: string, payload: InlineEditPayload, signal?: AbortSignal): Promise<InlineEditResponse> {
  const sentry_transaction_id = nanoid()
  const userFilesRag = payload.user_files_rag ? { user_file_ids: [...payload.user_files_rag.user_file_ids] } : null
  const filePaths = userFilesRag ? [] : payload.file_paths

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      signal,
      body: JSON.stringify({
        conversation_id: conversationId,
        edit_instruction: payload.edit_instruction,
        anchor_start: payload.anchor_start,
        anchor_end: payload.anchor_end,
        caselaw_search_filter: payload.caselaw_search_filter ?? null,
        lrr_search_filter: payload.lrr_search_filter ?? null,
        user_files_rag: userFilesRag,
        file_paths: filePaths,
      }),
    }

    const apiUrl = kPaxtonAppApiRESTBaseUrl() + kInlineEditPath.replace('{artifactVersionId}', payload.artifact_version_id)
    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()

    if (!response.ok) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error(`Inline edit failed - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: {
            requestUrl: apiUrl,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
          },
        })
      })
      if (response.status === 422) {
        throw new ApiError(response.status, 'Please select more text and try again.')
      }
      if (response.status === 400) {
        throw new ApiError(response.status, "Couldn't locate the selected text. Try selecting again.")
      }
      throw new ApiError(response.status, `Inline edit failed: ${response.status}`)
    }

    return InlineEditResponseSchema.parse(data)
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
